import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import {
  ActionsDialogue, Bouton, CaseACocher, Champ, classesBouton, Dialogue, EnTetePage,
  Etiquette, GrilleFormulaire, IconeChevronBas, IconeCorbeille, IconeCrayon,
  IconeFichePreparation, IconePlus, IconeTelecharger, Message, Selecteur, Squelette, Sur2,
  useRetours, Vide, ZoneTexte,
} from '@journal/ui';
import { useSession, messageErreur } from '../lib/session.tsx';
import { useDonnees } from '../lib/donnees.ts';
import { BlocObjectifs } from './fiches/Objectifs.tsx';
import { EditeurRiche, lireZone } from './fiches/EditeurRiche.tsx';
import { documentFiches } from './fiches/impression.ts';
import { useTelechargementPdf } from '../lib/impression.tsx';
import {
  descriptionAffichable, domainesDu, dureeDe, libelleDomaine, objectifsDe, pluriel, poids,
  sousDomainesDe,
  type Dossier, type Objectif, type Phase, type Programme, type ResumeSequence, type Seance,
  type Vocabulaire,
} from './fiches/modele.ts';

/**
 * Fiche de préparation — séquence → séances → phases.
 *
 * Reprise de la v1 à fonctionnalités et disposition égales : les séquences à
 * gauche, la séquence ouverte à droite ; ses séances par onglets, une seule à
 * l'écran ; chaque phase en bandeau — titre, durée, type, dispositif — avec sa
 * description en dessous, sur toute la largeur. Seul l'habillage change : il
 * est celui de l'application.
 *
 * ── Module indépendant ─────────────────────────────────────────────────────
 * Il lit le programme de la classe — domaines, sous-domaines, objectifs
 * proposés — et n'écrit nulle part ailleurs. Une fiche n'alimente ni le
 * cahier journal ni les progressions.
 */

type Formulaire =
  | { type: 'sequence'; sequence: Dossier['sequence'] | null }
  | { type: 'seance'; seance: Seance | null }
  | { type: 'phase'; seanceId: string; phase: Phase | null }
  | { type: 'impression' };

export function FichesPreparation() {
  const { api } = useSession();
  const { notifier, confirmer } = useRetours();

  const sequences = useDonnees<ResumeSequence[]>('/prep-sheets/sequences');
  const programme = useDonnees<Programme>('/curriculum/tree');
  const vocabulaire = useDonnees<Vocabulaire>('/prep-sheets/vocabulary');

  const [courante, setCourante] = useState<Dossier | null>(null);
  const [chargementFiche, setChargementFiche] = useState(false);
  const [onglet, setOnglet] = useState(0);
  const [formulaire, setFormulaire] = useState<Formulaire | null>(null);

  /**
   * Ouvre une séquence — ou la relit après une modification.
   *
   * `garderOnglet` : après avoir ajouté une phase à la séance 3, on reste sur la
   * séance 3. Revenir à la première à chaque geste obligeait à recliquer
   * l'onglet après chaque ligne saisie.
   */
  const ouvrir = useCallback(async (id: string, garderOnglet = false) => {
    if (!garderOnglet) setOnglet(0);
    setChargementFiche(true);
    try {
      setCourante(await api.requete<Dossier>(`/prep-sheets/sequences/${id}`));
    } catch (e) {
      notifier(messageErreur(e), 'erreur');
    } finally {
      setChargementFiche(false);
    }
  }, [api, notifier]);

  /** Relit la séquence ouverte et la liste de gauche, après une écriture. */
  async function rafraichir() {
    if (courante) await ouvrir(courante.sequence.id, true);
    await sequences.recharger();
  }

  async function supprimerSequence() {
    const s = courante?.sequence;
    if (!s) return;
    const ok = await confirmer({
      titre: 'Supprimer la séquence',
      message: `Supprimer « ${s.title} » et toutes ses séances ?`,
      libelleConfirmation: 'Supprimer',
      destructeur: true,
    });
    if (!ok) return;
    try {
      await api.requete(`/prep-sheets/sequences/${s.id}`, { methode: 'DELETE' });
      setCourante(null);
      notifier('Séquence supprimée');
      await sequences.recharger();
    } catch (e) {
      notifier(messageErreur(e), 'erreur');
    }
  }

  const liste = sequences.donnees ?? [];
  const seances = courante?.sessions ?? [];
  const ongletVisible = onglet < seances.length ? onglet : 0;

  return (
    <div className="flex flex-col">
      <EnTetePage
        titre="Fiche de préparation"
        contexte="Une séquence contient des séances, chaque séance contient des phases."
      />

      {sequences.erreur && <div className="mb-4"><Message ton="erreur">{sequences.erreur}</Message></div>}

      <div className="grid items-start gap-4 lg:grid-cols-[17rem_minmax(0,1fr)]">
        {/* ── Mes séquences ─────────────────────────────────────────────── */}
        <aside
          aria-label="Mes séquences"
          className={[
            'flex flex-col gap-3 rounded-xl border border-[var(--color-border)]',
            'bg-[var(--color-surface)] p-3 shadow-[var(--ombre-carte)] lg:sticky lg:top-4',
          ].join(' ')}
        >
          <div className="flex items-center justify-between gap-2">
            <h2 className="text-[11px] font-semibold uppercase tracking-wider text-[var(--color-fg-muted)]">
              Mes séquences
            </h2>
            <Bouton
              variante="primary"
              taille="sm"
              icone={<IconePlus taille={14} />}
              onClick={() => setFormulaire({ type: 'sequence', sequence: null })}
            >
              Séquence
            </Bouton>
          </div>
          {sequences.chargement && !sequences.donnees ? (
            <div className="flex flex-col gap-2"><Squelette className="h-12" /><Squelette className="h-12" /></div>
          ) : liste.length === 0 ? (
            <p className="py-2 text-sm text-[var(--color-fg-muted)]">
              Aucune séquence. Créez-en une pour commencer.
            </p>
          ) : (
            <ul className="flex flex-col gap-1.5">
              {liste.map((s) => {
                const active = s.id === courante?.sequence.id;
                return (
                  <li key={s.id}>
                    <button
                      type="button"
                      onClick={() => void ouvrir(s.id)}
                      aria-current={active ? 'true' : undefined}
                      className={[
                        'flex w-full flex-col gap-0.5 rounded-lg border px-3 py-2 text-left',
                        'focus-visible:outline-2 focus-visible:outline-[var(--color-focus)]',
                        active
                          ? 'border-[var(--color-primary)] bg-[var(--color-primary-soft)]'
                          : 'border-[var(--color-border)] hover:border-[var(--color-primary)] hover:bg-[var(--color-surface-hover)]',
                      ].join(' ')}
                    >
                      <span className="text-sm font-semibold text-[var(--color-fg)]">{s.title}</span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </aside>

        {/* ── La séquence ouverte ───────────────────────────────────────── */}
        <section aria-label="Séquence ouverte" className="min-w-0">
          {!courante ? (
            <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] shadow-[var(--ombre-carte)]">
              {chargementFiche ? (
                <div className="flex flex-col gap-3 p-6"><Squelette className="h-8 w-1/2" /><Squelette className="h-32" /></div>
              ) : (
                <Vide
                  titre="Choisissez une séquence à gauche, ou créez-en une."
                  icone={<IconeFichePreparation taille={20} />}
                >
                  Une séquence contient des séances, chaque séance contient des phases.
                </Vide>
              )}
            </div>
          ) : (
            <div className="flex flex-col gap-4">
              <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-5 shadow-[var(--ombre-carte)]">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <h2 className="text-xl font-semibold leading-tight text-[var(--color-fg)]">
                      {courante.sequence.title}
                    </h2>
                    <p className="mt-1 text-[13px] text-[var(--color-fg-muted)]">
                      {`${libelleDomaine(courante.sequence)} · ${pluriel(seances.length, 'séance')}`}
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Bouton
                      variante="ghost"
                      taille="sm"
                      icone={<IconeCrayon taille={14} />}
                      onClick={() => setFormulaire({ type: 'sequence', sequence: courante.sequence })}
                    >
                      Modifier
                    </Bouton>
                    <Bouton
                      variante="ghost"
                      taille="sm"
                      icone={<IconeTelecharger taille={14} />}
                      onClick={() => setFormulaire({ type: 'impression' })}
                    >
                      PDF…
                    </Bouton>
                    <Bouton
                      variante="danger"
                      taille="sm"
                      icone={<IconeCorbeille taille={14} />}
                      onClick={() => void supprimerSequence()}
                    >
                      Supprimer
                    </Bouton>
                  </div>
                </div>

                {courante.sequence.detail && (
                  <Bloc titre="Détail de la séquence">
                    <p className="whitespace-pre-wrap text-sm leading-relaxed">{courante.sequence.detail}</p>
                  </Bloc>
                )}
                {courante.sequence.objectives.length > 0 && (
                  <Bloc titre="Objectifs de la séquence">
                    <ListeObjectifs objectifs={courante.sequence.objectives} />
                  </Bloc>
                )}
              </div>

              <div className="flex items-center justify-between gap-2">
                <h2 className="text-base font-semibold text-[var(--color-fg)]">Séances</h2>
                <Bouton
                  variante="primary"
                  taille="sm"
                  icone={<IconePlus taille={14} />}
                  onClick={() => setFormulaire({ type: 'seance', seance: null })}
                >
                  Séance
                </Bouton>
              </div>

              {seances.length === 0 ? (
                <p className="text-sm text-[var(--color-fg-muted)]">Aucune séance. Ajoutez la première.</p>
              ) : (
                <>
                  {/*
                    * Une seule séance à l'écran : au-delà de trois ou quatre,
                    * la page devenait un rouleau où l'on ne retrouvait plus
                    * rien. L'impression, elle, les garde toutes à la suite.
                    */}
                  <div role="tablist" aria-label="Séances" className="flex flex-wrap gap-1.5">
                    {seances.map((se, i) => (
                      <button
                        key={se.id}
                        type="button"
                        role="tab"
                        aria-selected={i === ongletVisible}
                        title={se.title}
                        onClick={() => setOnglet(i)}
                        className={[
                          'rounded-lg border px-3 py-1.5 text-sm font-medium',
                          'focus-visible:outline-2 focus-visible:outline-[var(--color-focus)]',
                          i === ongletVisible
                            ? 'border-[var(--color-primary)] bg-[var(--color-primary)] text-[var(--color-primary-fg)]'
                            : 'border-[var(--color-border)] bg-[var(--color-surface)] text-[var(--color-fg-muted)] hover:text-[var(--color-fg)]',
                        ].join(' ')}
                      >
                        {`Séance ${i + 1}`}
                      </button>
                    ))}
                  </div>
                  <CarteSeance
                    key={seances[ongletVisible]!.id}
                    seance={seances[ongletVisible]!}
                    numero={ongletVisible + 1}
                    formats={vocabulaire.donnees?.formats ?? []}
                    maxOctets={vocabulaire.donnees?.maxBytes ?? 10 * 1024 * 1024}
                    surModifier={(se) => setFormulaire({ type: 'seance', seance: se })}
                    surNouvellePhase={(id) => setFormulaire({ type: 'phase', seanceId: id, phase: null })}
                    surModifierPhase={(id, p) => setFormulaire({ type: 'phase', seanceId: id, phase: p })}
                    surChangement={rafraichir}
                  />
                </>
              )}
            </div>
          )}
        </section>
      </div>

      {formulaire?.type === 'sequence' && (
        <DialogueSequence
          sequence={formulaire.sequence}
          programme={programme.donnees ?? []}
          surFermeture={() => setFormulaire(null)}
          surEnregistre={async (id) => {
            setFormulaire(null);
            notifier(formulaire.sequence ? 'Séquence modifiée' : 'Séquence créée');
            await sequences.recharger();
            await ouvrir(id, !!formulaire.sequence);
          }}
        />
      )}
      {formulaire?.type === 'seance' && courante && (
        <DialogueSeance
          seance={formulaire.seance}
          sequence={courante.sequence}
          programme={programme.donnees ?? []}
          surFermeture={() => setFormulaire(null)}
          surEnregistre={async () => {
            const nouvelle = !formulaire.seance;
            setFormulaire(null);
            notifier(nouvelle ? 'Séance ajoutée' : 'Séance modifiée');
            await rafraichir();
            // Une séance ajoutée s'ouvre : c'est elle qu'on va remplir.
            if (nouvelle) setOnglet(seances.length);
          }}
        />
      )}
      {formulaire?.type === 'phase' && (
        <DialoguePhase
          seanceId={formulaire.seanceId}
          phase={formulaire.phase}
          vocabulaire={vocabulaire.donnees}
          surFermeture={() => setFormulaire(null)}
          surEnregistre={async () => {
            notifier(formulaire.phase ? 'Phase modifiée' : 'Phase ajoutée');
            setFormulaire(null);
            await rafraichir();
          }}
        />
      )}
      {formulaire?.type === 'impression' && (
        <DialogueImpression
          sequences={liste}
          ouverte={courante?.sequence.id ?? null}
          surFermeture={() => setFormulaire(null)}
        />
      )}
    </div>
  );
}

// ── Morceaux d'affichage ─────────────────────────────────────────────────────

function Bloc({ titre, action, children }: { titre: string; action?: ReactNode; children: ReactNode }) {
  return (
    <div className="mt-4 border-t border-[var(--color-border)] pt-3">
      <div className="mb-1.5 flex items-center justify-between gap-2">
        <h3 className="text-[13px] font-semibold text-[var(--color-fg)]">{titre}</h3>
        {action}
      </div>
      {children}
    </div>
  );
}

function ListeObjectifs({ objectifs }: { objectifs: Objectif[] }) {
  return (
    <ul className="list-disc space-y-1 pl-5 text-sm leading-relaxed marker:text-[var(--color-primary)]">
      {objectifs.map((o, i) => <li key={i}>{o.text}</li>)}
    </ul>
  );
}

/** Un champ du bandeau d'une phase : son intitulé, puis sa valeur. */
function ChampPhase({ libelle, children, large }: { libelle: string; children: ReactNode; large?: boolean }) {
  return (
    <div
      className={[
        'min-w-0 border-l border-[var(--color-border)] px-3 py-1.5',
        // 9 rem : « Institutionnalisation », le plus long des types, tient
        // sur une ligne sans être coupé en plein mot.
        large ? 'flex-[2_1_11rem]' : 'flex-[1_1_9rem]',
      ].join(' ')}
    >
      <span className="block text-[10px] font-semibold uppercase tracking-wide text-[var(--color-primary-soft-fg)]">
        {libelle}
      </span>
      <strong className="block break-words text-[13px] font-semibold text-[var(--color-fg)]">
        {children}
      </strong>
    </div>
  );
}

function CarteSeance({
  seance: se, numero, formats, maxOctets,
  surModifier, surNouvellePhase, surModifierPhase, surChangement,
}: {
  seance: Seance;
  numero: number;
  formats: string[];
  maxOctets: number;
  surModifier: (se: Seance) => void;
  surNouvellePhase: (seanceId: string) => void;
  surModifierPhase: (seanceId: string, p: Phase) => void;
  surChangement: () => Promise<void>;
}) {
  const { api } = useSession();
  const { notifier, confirmer } = useRetours();
  const [envoi, setEnvoi] = useState(false);
  const total = dureeDe(se.phases);

  async function supprimerSeance() {
    const ok = await confirmer({
      titre: 'Supprimer la séance',
      message: `Supprimer la séance « ${se.title} », ses phases et ses pièces jointes ?`,
      libelleConfirmation: 'Supprimer',
      destructeur: true,
    });
    if (!ok) return;
    try {
      await api.requete(`/prep-sheets/sessions/${se.id}`, { methode: 'DELETE' });
      notifier('Séance supprimée');
      await surChangement();
    } catch (e) {
      notifier(messageErreur(e), 'erreur');
    }
  }

  async function deplacer(p: Phase, direction: -1 | 1) {
    try {
      await api.requete(`/prep-sheets/phases/${p.id}/move`, { methode: 'POST', corps: { direction } });
      await surChangement();
    } catch (e) {
      notifier(messageErreur(e), 'erreur');
    }
  }

  async function supprimerPhase(p: Phase) {
    if (!(await confirmer({
      titre: 'Supprimer la phase',
      message: `Supprimer la phase « ${p.title} » ?`,
      libelleConfirmation: 'Supprimer',
      destructeur: true,
    }))) return;
    try {
      await api.requete(`/prep-sheets/phases/${p.id}`, { methode: 'DELETE' });
      await surChangement();
    } catch (e) {
      notifier(messageErreur(e), 'erreur');
    }
  }

  /**
   * Joindre un document.
   *
   * Le format et le poids sont vérifiés ICI d'abord, avec le message du
   * serveur : un fichier de 40 Mo refusé après une minute d'envoi, c'est une
   * minute perdue. Le serveur vérifie de nouveau — c'est lui qui décide.
   *
   * Le champ est vidé à la fin, pour qu'on puisse rejoindre le même fichier.
   */
  async function joindre(champ: HTMLInputElement) {
    const fichier = champ.files?.[0];
    if (!fichier) return;
    const extension = fichier.name.includes('.') ? fichier.name.split('.').pop()!.toLowerCase() : '';
    try {
      if (formats.length && !formats.includes(extension)) {
        notifier(`Format « ${extension || 'sans extension'} » non accepté. Formats admis : ${formats.join(', ')}.`, 'erreur');
        return;
      }
      if (fichier.size > maxOctets) {
        notifier(`Fichier trop lourd : ${Math.round(fichier.size / 1_048_576)} Mo, la limite est de ${Math.round(maxOctets / 1_048_576)} Mo.`, 'erreur');
        return;
      }
      setEnvoi(true);
      await api.requete(`/prep-sheets/sessions/${se.id}/attachments`, { methode: 'POST', fichier });
      notifier('Pièce jointe ajoutée');
      await surChangement();
    } catch (e) {
      notifier(messageErreur(e), 'erreur');
    } finally {
      setEnvoi(false);
      champ.value = '';
    }
  }

  async function ouvrirPiece(id: string, nom: string) {
    try {
      await api.telecharger(`/prep-sheets/attachments/${id}`, nom);
    } catch (e) {
      notifier(messageErreur(e), 'erreur');
    }
  }

  async function retirerPiece(id: string, nom: string) {
    if (!(await confirmer({
      titre: 'Retirer la pièce jointe',
      message: `Retirer « ${nom} » de cette séance ?`,
      libelleConfirmation: 'Retirer',
      destructeur: true,
    }))) return;
    try {
      await api.requete(`/prep-sheets/attachments/${id}`, { methode: 'DELETE' });
      await surChangement();
    } catch (e) {
      notifier(messageErreur(e), 'erreur');
    }
  }

  return (
    <article
      role="tabpanel"
      aria-label={`Séance ${numero}`}
      className="rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-5 shadow-[var(--ombre-carte)]"
    >
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <span className="inline-block rounded-md bg-[var(--color-primary)] px-2 py-0.5 text-[11px] font-semibold text-[var(--color-primary-fg)]">
            {`Séance ${numero}`}
          </span>
          <h3 className="mt-1.5 text-lg font-semibold leading-tight text-[var(--color-fg)]">
            {se.title || 'Sans titre'}
          </h3>
        </div>
        <div className="flex items-center gap-1.5">
          {total > 0 && <Etiquette>{`${total} min`}</Etiquette>}
          <Bouton
            variante="ghost" taille="sm" iconeSeule icone={<IconeCrayon taille={15} />}
            aria-label="Modifier la séance" onClick={() => surModifier(se)}
          >
            Modifier la séance
          </Bouton>
          <Bouton
            variante="ghost" taille="sm" iconeSeule icone={<IconeCorbeille taille={15} />}
            aria-label="Supprimer la séance" onClick={() => void supprimerSeance()}
          >
            Supprimer la séance
          </Bouton>
        </div>
      </header>

      {se.objectives.length > 0 && (
        <Bloc titre="Objectifs"><ListeObjectifs objectifs={se.objectives} /></Bloc>
      )}
      {se.material && (
        <Bloc titre="Matériel"><p className="whitespace-pre-wrap text-sm leading-relaxed">{se.material}</p></Bloc>
      )}
      {se.comment && (
        <Bloc titre="Commentaire"><p className="whitespace-pre-wrap text-sm leading-relaxed">{se.comment}</p></Bloc>
      )}

      <Bloc
        titre="Pièces jointes"
        action={(
          <label
            title="Joindre un document à cette séance"
            className={`${classesBouton({ variante: 'ghost', taille: 'sm' })} cursor-pointer focus-within:outline-2 focus-within:outline-[var(--color-focus)]`}
          >
            <IconePlus taille={14} />
            {envoi ? 'Envoi…' : 'Ajouter'}
            <input
              type="file"
              className="sr-only"
              disabled={envoi}
              onChange={(e) => void joindre(e.currentTarget)}
            />
          </label>
        )}
      >
        {se.attachments.length === 0 ? (
          <p className="text-sm text-[var(--color-fg-muted)]">Aucune pièce jointe.</p>
        ) : (
          <ul className="flex flex-wrap gap-2">
            {se.attachments.map((a) => (
              <li
                key={a.id}
                className="inline-flex max-w-full items-center gap-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-raised)] py-1 pl-3 pr-1"
              >
                <button
                  type="button"
                  title={`Télécharger « ${a.name} »`}
                  onClick={() => void ouvrirPiece(a.id, a.name)}
                  className="min-w-0 truncate text-sm font-medium text-[var(--color-primary)] underline-offset-2 hover:underline"
                >
                  {a.name}
                </button>
                <span className="shrink-0 text-xs text-[var(--color-fg-muted)]">{poids(a.size)}</span>
                <Bouton
                  variante="discret" taille="sm" iconeSeule icone={<IconeCorbeille taille={13} />}
                  aria-label={`Retirer ${a.name}`} onClick={() => void retirerPiece(a.id, a.name)}
                >
                  Retirer
                </Bouton>
              </li>
            ))}
          </ul>
        )}
      </Bloc>

      <Bloc
        titre="Phases"
        action={(
          <Bouton variante="secondary" taille="sm" icone={<IconePlus taille={14} />} onClick={() => surNouvellePhase(se.id)}>
            Phase
          </Bouton>
        )}
      >
        {se.phases.length === 0 ? (
          <p className="text-sm text-[var(--color-fg-muted)]">Aucune phase.</p>
        ) : (
          <ol className="flex flex-col gap-2.5">
            {se.phases.map((p, i) => (
              <li key={p.id} className="overflow-hidden rounded-lg border border-[var(--color-border)]">
                {/* Les champs en bandeau, la description en dessous. */}
                <div className="flex flex-wrap items-stretch bg-[var(--color-primary-soft)]">
                  <span
                    aria-label={`Phase ${i + 1}`}
                    className="flex w-9 shrink-0 items-center justify-center bg-[var(--color-primary)] text-sm font-bold text-[var(--color-primary-fg)]"
                  >
                    {i + 1}
                  </span>
                  <ChampPhase libelle="Titre de la phase" large>{p.title || '-'}</ChampPhase>
                  <ChampPhase libelle="Durée (minutes)">{p.duration || '-'}</ChampPhase>
                  <ChampPhase libelle="Type de phase">{p.kind || '-'}</ChampPhase>
                  <ChampPhase libelle="Dispositif">{p.grouping || '-'}</ChampPhase>
                  <div className="flex items-center gap-0.5 border-l border-[var(--color-border)] px-1.5">
                    <Bouton
                      variante="discret" taille="sm" iconeSeule disabled={i === 0}
                      icone={<IconeChevronBas taille={15} style={{ transform: 'rotate(180deg)' }} />}
                      aria-label="Monter la phase" onClick={() => void deplacer(p, -1)}
                    >
                      Monter
                    </Bouton>
                    <Bouton
                      variante="discret" taille="sm" iconeSeule disabled={i === se.phases.length - 1}
                      icone={<IconeChevronBas taille={15} />}
                      aria-label="Descendre la phase" onClick={() => void deplacer(p, 1)}
                    >
                      Descendre
                    </Bouton>
                    <Bouton
                      variante="discret" taille="sm" iconeSeule icone={<IconeCrayon taille={14} />}
                      aria-label="Modifier la phase" onClick={() => surModifierPhase(se.id, p)}
                    >
                      Modifier
                    </Bouton>
                    <Bouton
                      variante="discret" taille="sm" iconeSeule icone={<IconeCorbeille taille={14} />}
                      aria-label="Supprimer la phase" onClick={() => void supprimerPhase(p)}
                    >
                      Supprimer
                    </Bouton>
                  </div>
                </div>
                <div className="px-3 py-2.5">
                  <span className="block text-[10px] font-semibold uppercase tracking-wide text-[var(--color-fg-muted)]">
                    Description
                  </span>
                  {p.description ? (
                    <div
                      className="fp-riche text-sm leading-relaxed text-[var(--color-fg)] [overflow-wrap:anywhere]"
                      // Nettoyée par le serveur à l'écriture : voir `descriptionAffichable`.
                      dangerouslySetInnerHTML={{ __html: descriptionAffichable(p.description) }}
                    />
                  ) : (
                    <p className="text-sm text-[var(--color-fg-subtle)]">-</p>
                  )}
                </div>
              </li>
            ))}
          </ol>
        )}
      </Bloc>
    </article>
  );
}

// ── Formulaires ──────────────────────────────────────────────────────────────

/** Les objectifs du programme pour un domaine, au format de la fenêtre de choix. */
function propositionsPour(programme: Programme, domaine: string, sousDomaine: string) {
  return () => {
    if (!domaine) return 'Choisissez d’abord un domaine.';
    const liste = objectifsDe(programme, domaine, sousDomaine);
    const intitule = sousDomaine ? `${domaine} › ${sousDomaine}` : domaine;
    if (liste.length === 0) return `Aucun objectif au programme pour « ${intitule} ».`;
    return { intitule, liste };
  };
}

function DialogueSequence({
  sequence, programme, surFermeture, surEnregistre,
}: {
  sequence: Dossier['sequence'] | null;
  programme: Programme;
  surFermeture: () => void;
  surEnregistre: (id: string) => Promise<void>;
}) {
  const { api } = useSession();
  const [titre, setTitre] = useState(sequence?.title ?? '');
  const [domaine, setDomaine] = useState(sequence?.domain ?? '');
  const [sousDomaine, setSousDomaine] = useState(sequence?.subdomain ?? '');
  const [detail, setDetail] = useState(sequence?.detail ?? '');
  const [objectifs, setObjectifs] = useState<Objectif[]>(sequence?.objectives ?? []);
  const [erreur, setErreur] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);

  /*
   * Un domaine enregistré qui n'est plus au programme reste proposé : sans
   * cela, rouvrir la fiche le viderait sans qu'on l'ait demandé.
   */
  const domaines = [...new Set([...domainesDu(programme), ...(sequence?.domain ? [sequence.domain] : [])])];
  const sousDomaines = [...new Set([
    ...sousDomainesDe(programme, domaine),
    ...(sequence?.subdomain && sequence.domain === domaine ? [sequence.subdomain] : []),
  ])];

  async function enregistrer() {
    if (!titre.trim()) { setErreur('Donnez un titre à la séquence.'); return; }
    setEnCours(true);
    setErreur(null);
    const corps = { title: titre, domain: domaine, subdomain: sousDomaine, detail, objectives: objectifs };
    try {
      if (sequence) {
        await api.requete(`/prep-sheets/sequences/${sequence.id}`, { methode: 'PUT', corps });
        await surEnregistre(sequence.id);
      } else {
        const r = await api.requete<{ id: string }>('/prep-sheets/sequences', { methode: 'POST', corps });
        await surEnregistre(r.id);
      }
    } catch (e) {
      setErreur(messageErreur(e));
    } finally {
      setEnCours(false);
    }
  }

  return (
    <Dialogue
      ouvert
      titre={sequence ? 'Modifier la séquence' : 'Nouvelle séquence'}
      surFermeture={surFermeture}
      largeur="lg"
    >
      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => { e.preventDefault(); void enregistrer(); }}
      >
        <Champ
          libelle="Titre de la séquence"
          required
          placeholder="Ex : Le portrait"
          value={titre}
          onChange={(e) => setTitre(e.target.value)}
          autoFocus
        />
        <GrilleFormulaire>
          <Selecteur
            libelle="Domaine"
            value={domaine}
            aide={domaines.length ? undefined : 'Aucun domaine : le programme de la classe est vide.'}
            options={[
              { valeur: '', texte: '- Choisir dans le programme de l’année -' },
              ...domaines.map((d) => ({ valeur: d, texte: d })),
            ]}
            onChange={(e) => {
              // Le sous-domaine dépend du domaine : changer l'un vide l'autre,
              // sans quoi la fiche annoncerait « Mathématiques › Lecture ».
              setDomaine(e.target.value);
              setSousDomaine('');
            }}
          />
          <Selecteur
            libelle="Sous-domaine"
            value={sousDomaine}
            disabled={!domaine}
            options={[
              { valeur: '', texte: '- Tout le domaine -' },
              ...sousDomaines.map((d) => ({ valeur: d, texte: d })),
            ]}
            onChange={(e) => setSousDomaine(e.target.value)}
          />
          <Sur2>
            <ZoneTexte
              libelle="Détail de la séquence"
              rows={3}
              placeholder="Ce que la séquence vise, son déroulé général…"
              value={detail}
              onChange={(e) => setDetail(e.target.value)}
            />
          </Sur2>
        </GrilleFormulaire>
        <BlocObjectifs
          quoi="séquence"
          objectifs={objectifs}
          surChangement={setObjectifs}
          propositions={propositionsPour(programme, domaine, sousDomaine)}
        />
        {erreur && <Message ton="erreur">{erreur}</Message>}
        <ActionsDialogue>
          <Bouton variante="ghost" onClick={surFermeture}>Annuler</Bouton>
          <Bouton variante="primary" type="submit" chargement={enCours}>
            {sequence ? 'Enregistrer' : 'Créer la séquence'}
          </Bouton>
        </ActionsDialogue>
      </form>
    </Dialogue>
  );
}

function DialogueSeance({
  seance, sequence, programme, surFermeture, surEnregistre,
}: {
  seance: Seance | null;
  sequence: Dossier['sequence'];
  programme: Programme;
  surFermeture: () => void;
  surEnregistre: () => Promise<void>;
}) {
  const { api } = useSession();
  const [titre, setTitre] = useState(seance?.title ?? '');
  const [materiel, setMateriel] = useState(seance?.material ?? '');
  const [commentaire, setCommentaire] = useState(seance?.comment ?? '');
  const [objectifs, setObjectifs] = useState<Objectif[]>(seance?.objectives ?? []);
  const [erreur, setErreur] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);

  async function enregistrer() {
    if (!titre.trim()) { setErreur('Donnez un titre à la séance.'); return; }
    setEnCours(true);
    setErreur(null);
    const corps = { title: titre, material: materiel, comment: commentaire, objectives: objectifs };
    try {
      if (seance) {
        await api.requete(`/prep-sheets/sessions/${seance.id}`, { methode: 'PUT', corps });
      } else {
        await api.requete('/prep-sheets/sessions', {
          methode: 'POST', corps: { ...corps, sequenceId: sequence.id },
        });
      }
      await surEnregistre();
    } catch (e) {
      setErreur(messageErreur(e));
    } finally {
      setEnCours(false);
    }
  }

  return (
    <Dialogue
      ouvert
      titre={seance ? 'Modifier la séance' : 'Nouvelle séance'}
      surFermeture={surFermeture}
      largeur="lg"
    >
      <form className="flex flex-col gap-4" onSubmit={(e) => { e.preventDefault(); void enregistrer(); }}>
        <Champ
          libelle="Titre de la séance"
          required
          placeholder="Ex : Repérer les adjectifs"
          value={titre}
          onChange={(e) => setTitre(e.target.value)}
          autoFocus
        />
        {/* Sur une séance, le domaine est celui de sa séquence : la fiche reste
            cohérente de bout en bout. */}
        <BlocObjectifs
          quoi="séance"
          objectifs={objectifs}
          surChangement={setObjectifs}
          propositions={propositionsPour(programme, sequence.domain, sequence.subdomain)}
        />
        <ZoneTexte
          libelle="Matériel"
          rows={2}
          placeholder="Affiches, ardoises, fiche élève…"
          value={materiel}
          onChange={(e) => setMateriel(e.target.value)}
        />
        <ZoneTexte
          libelle="Commentaire"
          rows={2}
          placeholder="Différenciation, points de vigilance…"
          value={commentaire}
          onChange={(e) => setCommentaire(e.target.value)}
        />
        {erreur && <Message ton="erreur">{erreur}</Message>}
        <ActionsDialogue>
          <Bouton variante="ghost" onClick={surFermeture}>Annuler</Bouton>
          <Bouton variante="primary" type="submit" chargement={enCours}>
            {seance ? 'Enregistrer' : 'Ajouter la séance'}
          </Bouton>
        </ActionsDialogue>
      </form>
    </Dialogue>
  );
}

function DialoguePhase({
  seanceId, phase, vocabulaire, surFermeture, surEnregistre,
}: {
  seanceId: string;
  phase: Phase | null;
  vocabulaire: Vocabulaire | null;
  surFermeture: () => void;
  surEnregistre: () => Promise<void>;
}) {
  const { api } = useSession();
  const [titre, setTitre] = useState(phase?.title ?? '');
  const [duree, setDuree] = useState(String(phase ? phase.duration : 10));
  const [type, setType] = useState(phase?.kind ?? '');
  const [dispositif, setDispositif] = useState(phase?.grouping ?? '');
  const zone = useRef<HTMLDivElement>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);

  async function enregistrer() {
    if (!titre.trim()) { setErreur('Donnez un titre à la phase.'); return; }
    setEnCours(true);
    setErreur(null);
    const corps = {
      title: titre,
      duration: Math.min(480, Math.max(0, Number.parseInt(duree, 10) || 0)),
      kind: type,
      grouping: dispositif,
      description: lireZone(zone.current),
    };
    try {
      if (phase) {
        await api.requete(`/prep-sheets/phases/${phase.id}`, { methode: 'PUT', corps });
      } else {
        await api.requete('/prep-sheets/phases', { methode: 'POST', corps: { ...corps, sessionId: seanceId } });
      }
      await surEnregistre();
    } catch (e) {
      setErreur(messageErreur(e));
    } finally {
      setEnCours(false);
    }
  }

  const options = (liste: string[] | undefined, actuelle: string) => [
    { valeur: '', texte: '-' },
    ...[...new Set([...(liste ?? []), ...(actuelle ? [actuelle] : [])])].map((v) => ({ valeur: v, texte: v })),
  ];

  return (
    <Dialogue
      ouvert
      titre={phase ? 'Modifier la phase' : 'Nouvelle phase'}
      surFermeture={surFermeture}
      largeur="lg"
    >
      <form className="flex flex-col gap-4" onSubmit={(e) => { e.preventDefault(); void enregistrer(); }}>
        <GrilleFormulaire>
          <Champ
            libelle="Titre de la phase"
            required
            placeholder="Ex : Mise en situation"
            value={titre}
            onChange={(e) => setTitre(e.target.value)}
            autoFocus
          />
          <Champ
            libelle="Durée (minutes)"
            type="number"
            min={0}
            max={480}
            step={5}
            value={duree}
            onChange={(e) => setDuree(e.target.value)}
          />
          <Selecteur
            libelle="Type de phase"
            value={type}
            options={options(vocabulaire?.kinds, type)}
            onChange={(e) => setType(e.target.value)}
          />
          <Selecteur
            libelle="Dispositif"
            value={dispositif}
            options={options(vocabulaire?.groupings, dispositif)}
            onChange={(e) => setDispositif(e.target.value)}
          />
        </GrilleFormulaire>
        <EditeurRiche
          libelle="Description"
          valeurInitiale={phase?.description ?? ''}
          zone={zone}
          indication="Ce que fait le maître, ce que font les élèves…"
        />
        {erreur && <Message ton="erreur">{erreur}</Message>}
        <ActionsDialogue>
          <Bouton variante="ghost" onClick={surFermeture}>Annuler</Bouton>
          <Bouton variante="primary" type="submit" chargement={enCours}>
            {phase ? 'Enregistrer' : 'Ajouter la phase'}
          </Bouton>
        </ActionsDialogue>
      </form>
    </Dialogue>
  );
}

/**
 * Télécharger des fiches en PDF.
 *
 * On imprime rarement une seule séquence : en fin de période, on sort le
 * dossier entier. La fenêtre laisse donc cocher ce qu'on veut, la séquence
 * ouverte cochée d'avance — c'est le cas le plus fréquent. Les fiches sont
 * chargées à l'ouverture : le clic n'attend plus que la mise en page.
 */
function DialogueImpression({
  sequences, ouverte, surFermeture,
}: {
  sequences: ResumeSequence[];
  ouverte: string | null;
  surFermeture: () => void;
}) {
  const { api } = useSession();
  const [cochees, setCochees] = useState<Set<string>>(() => new Set(ouverte ? [ouverte] : []));
  const [dossiers, setDossiers] = useState<Map<string, Dossier> | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const pdf = useTelechargementPdf();

  // Les identifiants, et non le tableau : il est recréé à chaque rendu.
  const ids = sequences.map((s) => s.id).join(',');
  useEffect(() => {
    let vivant = true;
    void (async () => {
      try {
        const lus = await Promise.all(ids.split(',').filter(Boolean).map((id) =>
          api.requete<Dossier>(`/prep-sheets/sequences/${id}`)));
        if (vivant) setDossiers(new Map(lus.map((d) => [d.sequence.id, d])));
      } catch (e) {
        if (vivant) setErreur(messageErreur(e));
      }
    })();
    return () => { vivant = false; };
  }, [api, ids]);

  function basculer(id: string, oui: boolean) {
    setCochees((actuelles) => {
      const suivantes = new Set(actuelles);
      if (oui) suivantes.add(id); else suivantes.delete(id);
      return suivantes;
    });
  }

  async function lancer() {
    if (!dossiers) return;
    if (cochees.size === 0) { setErreur('Cochez au moins une séquence.'); return; }
    // Dans l'ordre de la liste, pas dans celui des clics.
    const choisis = sequences.map((s) => dossiers.get(s.id)).filter((d): d is Dossier => !!d && cochees.has(d.sequence.id));
    const utiles = choisis.filter((d) => d.sessions.length || d.sequence.objectives.length || d.sequence.detail);
    if (utiles.length === 0) { setErreur('Ces séquences sont vides : rien à imprimer.'); return; }
    await pdf.telecharger(documentFiches(utiles));
    surFermeture();
  }

  return (
    <Dialogue
      ouvert
      titre="Télécharger des fiches de préparation"
      description="Cochez les séquences à mettre dans le PDF. Chacune commence sur une page neuve, avec sa page de garde puis ses séances."
      surFermeture={surFermeture}
      largeur="md"
    >
      <div className="flex flex-col gap-3">
        <div className="flex gap-2">
          <Bouton variante="ghost" taille="sm" onClick={() => setCochees(new Set(sequences.map((s) => s.id)))}>
            Tout cocher
          </Bouton>
          <Bouton variante="ghost" taille="sm" onClick={() => setCochees(new Set())}>
            Tout décocher
          </Bouton>
        </div>
        <ul className="flex flex-col gap-2.5">
          {sequences.map((s) => (
            <li key={s.id}>
              <CaseACocher
                libelle={<strong className="font-semibold">{s.title}</strong>}
                description={`${libelleDomaine(s)} · ${pluriel(s.sessionCount, 'séance')}`}
                checked={cochees.has(s.id)}
                onChange={(e) => basculer(s.id, e.target.checked)}
              />
            </li>
          ))}
        </ul>
        {(erreur ?? pdf.erreur) && <Message ton="erreur">{erreur ?? pdf.erreur}</Message>}
      </div>
      <ActionsDialogue>
        <Bouton variante="ghost" onClick={surFermeture}>Annuler</Bouton>
        <Bouton
          variante="primary"
          icone={<IconeTelecharger taille={15} />}
          chargement={(!dossiers && !erreur) || pdf.enCours}
          disabled={!dossiers}
          onClick={() => void lancer()}
        >
          Télécharger le PDF
        </Bouton>
      </ActionsDialogue>
    </Dialogue>
  );
}
